import {
  Popover,
  Stack,
  Box,
  Text,
  styled,
  useTheme,
  Button,
} from '@ltvco/refresh-lib/theme';
import { CollectionType } from '@ltvco/refresh-lib/v2';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useState, useEffect } from 'react';

import { COLLECTION_TYPE_LABELS } from 'components/ChatBV/constants';

import { collectionTypeIcons, StyledButton } from './MessageTeaser';

const StyledStack = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
}));

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.divider,
  borderRadius: theme.spacing(2.5),
  height: '40px',
  padding: `0 ${theme.spacing(0.5)}`,
}));

const ToggleButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  color: theme.palette.text.secondary,
  border: 'none',
  width: '50%',
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

type SourceSelectProps = {
  collectionType: CollectionType;
  setCollectionType: (collectionType: CollectionType) => void;
};

export const SourceSelect = ({
  collectionType,
  setCollectionType,
}: SourceSelectProps) => {
  const theme = useTheme();
  const [createAnchorEl, setCreateAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const deceasedTypes = [
    CollectionType.OBITUARIES,
    CollectionType.FAMILY_SEARCH,
  ];
  const [isDeceased, setIsDeceased] = useState(
    deceasedTypes.includes(collectionType)
  );
  const open = Boolean(createAnchorEl);

  // Reset tab when popover opens so it reflects the current collectionType
  useEffect(() => {
    if (createAnchorEl) {
      setIsDeceased(deceasedTypes.includes(collectionType));
    }
  }, [createAnchorEl]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setCreateAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setCreateAnchorEl(null);
  };

  const livingSourceOptions = [
    {
      display: 'Find personal contact details',
      value: CollectionType.PUBLIC_RECORDS,
    },
    {
      display: 'View professional info',
      value: CollectionType.CONTACTS,
    },
    {
      display: 'Find legal case records',
      value: CollectionType.COURT_CASES,
    },
    {
      display: 'View court rulings',
      value: CollectionType.COURT_OPINIONS,
    },
  ];

  const deceasedSourceOptions = [
    {
      display: 'Find personal contact details',
      value: CollectionType.PUBLIC_RECORDS,
    },
    {
      display: 'Find memorial notices',
      value: CollectionType.OBITUARIES,
    },
    {
      display: 'Explore family history',
      value: CollectionType.FAMILY_SEARCH,
    },
  ];

  const handleSourceClick = (value: CollectionType) => {
    setCollectionType(value);
    handleClose();
  };

  const sourceOptions = () =>
    isDeceased ? deceasedSourceOptions : livingSourceOptions;

  return (
    <>
      <StyledButton
        variant="outlined"
        size="small"
        onClick={handleClick}
        sx={{ cursor: 'pointer' }}
      >
        Source: {COLLECTION_TYPE_LABELS[collectionType]}
        <KeyboardArrowDown sx={{ ml: 0.5 }} />
      </StyledButton>
      <Popover
        open={open}
        anchorEl={createAnchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          '& .MuiPaper-root': {
            width: '300px',
            borderRadius: theme.spacing(2),
          },
        }}
      >
        <StyledStack spacing={3} p={2}>
          <Text variant="h4" fontWeight="bold">
            Source
          </Text>
          <StyledBox>
            <ToggleButton
              variant={isDeceased ? 'outlined' : 'contained'}
              size="small"
              onClick={() => setIsDeceased(false)}
              aria-pressed={!isDeceased}
              sx={
                isDeceased
                  ? {}
                  : {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      boxShadow: '0px 2px 1px -1px #00000033',
                      '&:hover': {
                        backgroundColor: theme.palette.background.paper,
                      },
                      '&:focus-visible': {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: '2px',
                      },
                    }
              }
            >
              Living
            </ToggleButton>
            <ToggleButton
              variant={isDeceased ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setIsDeceased(true)}
              aria-pressed={isDeceased}
              sx={
                isDeceased
                  ? {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      boxShadow: '0px 2px 1px -1px #00000033',
                      '&:hover': {
                        backgroundColor: theme.palette.background.paper,
                      },
                      '&:focus-visible': {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: '2px',
                      },
                    }
                  : {}
              }
            >
              Deceased
            </ToggleButton>
          </StyledBox>
          {sourceOptions().map((option) => (
            <Stack
              key={option.value}
              spacing={1}
              onClick={() => handleSourceClick(option.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSourceClick(option.value);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`Select ${
                COLLECTION_TYPE_LABELS[option.value]
              } source`}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.palette.rats.light,
                },
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: '2px',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {collectionTypeIcons[option.value]}
                <Text variant="body1">
                  {COLLECTION_TYPE_LABELS[option.value]}
                </Text>
              </Box>
              <Text variant="body2">{option.display}</Text>
            </Stack>
          ))}
        </StyledStack>
      </Popover>
    </>
  );
};
